import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { usePublicBlogPost } from '@/hooks/blog/usePublicBlogPost';
import { format } from 'date-fns';
import { BlogOpenGraph } from '@/components/blog/BlogOpenGraph';
import { BlogStructuredData } from '@/components/blog/BlogStructuredData';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdjacentPosts } from '@/hooks/blog/useAdjacentPosts';
import { useRelatedPosts } from '@/hooks/blog/useRelatedPosts';
import { usePopularPosts } from '@/hooks/blog/usePopularPosts';
import { BlogPostNavigation } from '@/components/blog/public/BlogPostNavigation';
import { RelatedPostsSidebar } from '@/components/blog/public/RelatedPostsSidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { calculateReadingTime } from '@/lib/blog-utils';
import { Helmet } from 'react-helmet';
import { BlogPostHeader } from '@/components/blog/public/BlogPostHeader';
import { BlogItinerariesCard } from '@/components/blog/public/BlogItinerariesCard';
import { BlogPostContent } from '@/components/blog/public/BlogPostContent';
import { BlogPostTags } from '@/components/blog/public/BlogPostTags';
import { BlogPostFooter } from '@/components/blog/public/BlogPostFooter';
import { BlogAffiliateRecommendations } from '@/components/blog/BlogAffiliateRecommendations';
import { BreadcrumbStructuredData, BreadcrumbItemType } from '@/components/structured-data/BreadcrumbStructuredData';
import { Breadcrumb, BreadcrumbItem as UIBreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { ReadingProgressBar } from '@/components/ui/reading-progress-bar';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useLoadingCoordinator } from '@/hooks/blog/useLoadingCoordinator';

export default function PublicBlogPost() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const renderCount = useRef(0);
  const mountRef = useRef(false);
  
  useEffect(() => {
    renderCount.current += 1;
    
    if (!mountRef.current) {
      console.log('[DEBUG] PublicBlogPost MOUNTED with slug:', slug);
      mountRef.current = true;
    }
    
    console.log(`[DEBUG] PublicBlogPost RENDER #${renderCount.current} with slug:`, slug);
  });
  
  const { updateLoadingState, startNavigation, isLoading, isNavigating, loadingStates } = useLoadingCoordinator();
  
  const { post, loading: postLoading, error } = usePublicBlogPost(slug);
  
  const tagIds = useMemo(() => (post?.tags?.map(tag => tag.id).filter(Boolean) as string[]) || [], [post?.id]);
  
  const { previousPost, nextPost, loading: adjacentPostsLoading } = useAdjacentPosts(
    post?.id || null, 
    post?.created_at || null
  );
  
  const { posts: relatedPosts, loading: relatedPostsLoading } = useRelatedPosts(post?.id || null, tagIds, 3);
  
  const { posts: popularPosts } = usePopularPosts(3);
  
  const baseUrl = window.location.origin;
  const currentUrl = `${baseUrl}/blog/${slug}`;
  
  // Create breadcrumb items for structured data
  const breadcrumbItems: BreadcrumbItemType[] = [
    { name: 'Home', url: '/', position: 1 },
    { name: 'Blog', url: '/blog', position: 2 },
  ];
  
  if (post) {
    breadcrumbItems.push({
      name: post.title || '',
      url: `/blog/${post.slug || ''}`,
      position: 3
    });
  }

  useEffect(() => {
    if (post && post.id) {
      const trackPageView = async () => {
        console.log('[DEBUG] PublicBlogPost tracking page view for:', post.title);
        try {
          await fetch('/api/analytics/pageview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              postId: post.id, 
              title: post.title,
              path: location.pathname
            })
          });
        } catch (err) {
          console.error('Error tracking page view:', err);
        }
      };
      
      trackPageView();
    }
  }, [post?.id, post?.title]);

  const handlePostNavigation = useCallback(() => {
    console.log('[DEBUG] Starting navigation, current states:', loadingStates);
    startNavigation();
  }, [startNavigation, loadingStates]);

  // Update loading coordinator with individual loading states
  useEffect(() => {
    console.log('Post loading state:', postLoading);
    updateLoadingState('post', postLoading);
  }, [postLoading, updateLoadingState]);

  useEffect(() => {
    console.log('Adjacent posts loading state:', adjacentPostsLoading);
    updateLoadingState('adjacentPosts', adjacentPostsLoading);
  }, [adjacentPostsLoading, updateLoadingState]);

  useEffect(() => {
    console.log('Related posts loading state:', relatedPostsLoading);
    updateLoadingState('relatedPosts', relatedPostsLoading);
  }, [relatedPostsLoading, updateLoadingState]);

  // Debug loading states
  useEffect(() => {
    console.log('Loading coordination:', { loadingStates, isLoading, isNavigating });
  }, [loadingStates, isLoading, isNavigating]);

  const formattedDate = post?.created_at 
    ? format(new Date(post.created_at), 'MMMM d, yyyy') 
    : '';
    
  const authorName = post?.author 
    ? `${post.author.first_name || ''} ${post.author.last_name || ''}`.trim() 
    : 'Trotting the World';
    
  const readingTimeMinutes = post?.content 
    ? calculateReadingTime(post.content) 
    : 0;
    
  useEffect(() => {
    console.log('[DEBUG] PublicBlogPost print styles effect running');
    const style = document.createElement('style');
    style.id = 'print-styles';
    style.innerHTML = `
      @media print {
        header, footer, .blog-navigation, .related-posts, .social-share, .back-button {
          display: none !important;
        }
        
        .print-container {
          margin: 0;
          padding: 0;
        }
        
        .packing-list {
          page-break-before: always;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      console.log('[DEBUG] PublicBlogPost print styles effect cleanup');
      const printStyle = document.getElementById('print-styles');
      if (printStyle) {
        document.head.removeChild(printStyle);
      }
    };
  }, []);

  const handleBackClick = () => {
    console.log("[DEBUG] PublicBlogPost Back to blog clicked");
    navigate('/blog');
  };

  const handleTagClick = (tagName: string) => {
    console.log(`[DEBUG] PublicBlogPost Tag clicked: ${tagName}`);
    navigate(`/blog?tag=${tagName}`);
  };

  useEffect(() => {
    return () => {
      console.log('[DEBUG] PublicBlogPost UNMOUNTED');
    };
  }, []);

  // Determine if we should show loading states (either initial loading or navigating between posts)
  const isLoadingContent = isLoading || isNavigating;

  return (
    <>
      {/* Reading Progress Bar - shown during loading or navigation */}
      <ReadingProgressBar isLoading={isLoadingContent} />
      
      {post && (
        <>
          <BlogOpenGraph post={post} baseUrl={baseUrl} />
          <BlogStructuredData post={post} baseUrl={baseUrl} />
          <BreadcrumbStructuredData items={breadcrumbItems} baseUrl={baseUrl} />
        </>
      )}

      <div className={`container mx-auto print-container transition-opacity duration-300 ${isLoadingContent && !post ? 'opacity-70' : 'opacity-100'}`}>
        {/* UI Breadcrumbs */}
        {post && (
          <Breadcrumb className="mb-4 mt-6">
            <BreadcrumbList>
              <UIBreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </UIBreadcrumbItem>
              <BreadcrumbSeparator />
              <UIBreadcrumbItem>
                <BreadcrumbLink href="/blog">Blog</BreadcrumbLink>
              </UIBreadcrumbItem>
              <BreadcrumbSeparator />
              <UIBreadcrumbItem>
                <BreadcrumbPage>{post.title}</BreadcrumbPage>
              </UIBreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        )}
      
        <Button 
          variant="ghost" 
          size="sm"
          className="mb-6 back-button"
          onClick={handleBackClick}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to all posts
        </Button>
        
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            {(postLoading && !post) ? (
              <div className="space-y-4 animate-pulse">
                <Skeleton className="h-10 w-3/4" />
                <div className="flex items-center gap-4 my-2">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-6 w-32" />
                </div>
                <AspectRatio ratio={16/9}>
                  <Skeleton className="h-full w-full" />
                </AspectRatio>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <h2 className="text-2xl font-bold text-destructive mb-2">
                  {error}
                </h2>
                <p className="text-muted-foreground mb-6">
                  The blog post you're looking for couldn't be found or isn't available.
                </p>
                <Button onClick={() => navigate('/blog')}>
                  Browse all posts
                </Button>
              </div>
            ) : post ? (
              <div className={`transition-opacity duration-300 ${isNavigating ? 'opacity-70' : 'opacity-100'}`}>
                <BlogPostHeader
                  post={post}
                  formattedDate={formattedDate}
                  authorName={authorName}
                  readingTimeMinutes={readingTimeMinutes}
                  currentUrl={currentUrl}
                  isNavigating={isNavigating}
                />
                
                {post.itineraries && post.itineraries.length > 0 && (
                  <BlogItinerariesCard 
                    itineraries={post.itineraries}
                    isNavigating={isNavigating}
                  />
                )}
                
                <BlogPostContent
                  content={post.content || ''}
                  blogPostId={post.id}
                  loading={postLoading}
                  isNavigating={isNavigating}
                />
                
                <BlogPostFooter
                  title={post.title}
                  url={currentUrl}
                  description={post.excerpt || ''}
                  image={post.featured_image || ''}
                />
                
                {post.tags && post.tags.length > 0 && (
                  <BlogAffiliateRecommendations 
                    tags={post.tags}
                    isNavigating={isNavigating}
                  />
                )}
                
                {post.tags && post.tags.length > 0 && (
                  <BlogPostTags
                    tags={post.tags}
                    isNavigating={isNavigating}
                    onTagClick={handleTagClick}
                  />
                )}
                
                <div className="blog-navigation">
                  <BlogPostNavigation 
                    previousPost={previousPost} 
                    nextPost={nextPost} 
                    loading={adjacentPostsLoading}
                    isNavigating={isNavigating}
                    onNavigate={handlePostNavigation}
                  />
                </div>
                
                {isMobile && post.tags && (
                  <div className="mt-8 lg:hidden related-posts">
                    <RelatedPostsSidebar 
                      relatedPosts={relatedPosts} 
                      popularPosts={popularPosts}
                      tags={post.tags}
                      onPostClick={handlePostNavigation}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <h2 className="text-2xl font-bold mb-2">
                  Blog Post Not Found
                </h2>
                <p className="text-muted-foreground mb-6">
                  The blog post you're looking for couldn't be found or isn't available.
                </p>
                <Button onClick={() => navigate('/blog')}>
                  Browse all posts
                </Button>
              </div>
            )}
          </div>
          
          {!isMobile && post && post.tags && (
            <div className="lg:w-80 shrink-0 related-posts">
              <div className="sticky top-24">
                <RelatedPostsSidebar 
                  relatedPosts={relatedPosts} 
                  popularPosts={popularPosts}
                  tags={post.tags}
                  onPostClick={handlePostNavigation}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
